import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setActiveBaseId, getActiveBaseId } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BaseContext = createContext(null);

/**
 * Loads the bases the signed-in user may access and tracks the active
 * base selected in the header switcher. "all" means "every base I can
 * see". Changing the active base persists it (localStorage, via the api
 * module) and reloads the app so every screen re-fetches with the new
 * X-Base-Id header — a reliable, low-surface way to re-scope all data.
 */
export function BaseProvider({ children }) {
  const { user } = useAuth();
  const [bases, setBases] = useState([]);
  const [allAccess, setAllAccess] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [defaultBaseId, setDefaultBaseId] = useState(null);
  const [activeBase, setActiveBase] = useState(() => getActiveBaseId());
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/bases");
      const list = data.bases || [];
      setBases(list);
      setAllAccess(!!data.all_access);
      setCanManage(!!data.can_manage);
      setDefaultBaseId(data.default_base_id || null);

      // Reconcile the persisted active base against what the user may see.
      const current = getActiveBaseId();
      const validIds = list.map((b) => b.id);
      if (current !== "all" && !validIds.includes(current)) {
        // Previously-selected base is no longer accessible — reset.
        setActiveBaseId("all");
        setActiveBase("all");
      }
      // Base-scoped users with exactly one base: pin to it so the UI is
      // unambiguous (no "all" option that would look empty).
      if (!data.all_access && list.length === 1 && current !== list[0].id) {
        setActiveBaseId(list[0].id);
        setActiveBase(list[0].id);
      }
    } catch (_e) {
      // Non-fatal — bases are an overlay; the app still works with "all".
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const changeBase = useCallback((baseId) => {
    const next = baseId || "all";
    if (next === getActiveBaseId()) return;
    setActiveBaseId(next);
    setActiveBase(next);
    // Hard reload so every page re-fetches scoped to the new base.
    try { window.location.reload(); } catch (_e) { /* noop */ }
  }, []);

  const activeBaseName = (() => {
    if (activeBase === "all") return allAccess || bases.length > 1 ? "All Bases" : (bases[0]?.name || "All Bases");
    return bases.find((b) => b.id === activeBase)?.name || "All Bases";
  })();

  // The base whose country drives the weather + local-time chips + flag.
  // When a specific base is active → that base; when "all" is selected →
  // the user's default base, else the primary, else the first base.
  const activeBaseMeta = (() => {
    if (activeBase && activeBase !== "all") {
      return bases.find((b) => b.id === activeBase) || null;
    }
    return (
      bases.find((b) => b.id === defaultBaseId) ||
      bases.find((b) => b.is_primary) ||
      bases[0] ||
      null
    );
  })();

  const value = {
    bases, allAccess, canManage, loaded, defaultBaseId,
    activeBase, activeBaseName, activeBaseMeta, changeBase, reloadBases: load,
    // Whether to show the switcher at all (needs >1 option or all-access).
    showSwitcher: allAccess || bases.length > 1,
  };

  return <BaseContext.Provider value={value}>{children}</BaseContext.Provider>;
}

export function useBase() {
  const ctx = useContext(BaseContext);
  if (!ctx) {
    // Safe fallback when used outside the provider (e.g. public pages).
    return {
      bases: [], allAccess: false, canManage: false, loaded: true, defaultBaseId: null,
      activeBase: "all", activeBaseName: "All Bases", activeBaseMeta: null,
      changeBase: () => {}, reloadBases: () => {}, showSwitcher: false,
    };
  }
  return ctx;
}
